// Client-side image compression for document/camera uploads.
// Resizes to max 2000px and re-encodes as JPEG targeting < 2 MB so
// 5–15 MB phone photos never hit proxy/memory limits.
// Non-images (PDF) and undecodable formats (e.g. HEIC on some browsers)
// pass through unchanged.

const MAX_DIM = 2000;
const TARGET_BYTES = 2 * 1024 * 1024;
const QUALITY_STEPS = [0.85, 0.7, 0.55, 0.4];
async function decodeToBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall through to <img> decode */
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

export async function compressImageFile(file, { maxDim = MAX_DIM, targetBytes = TARGET_BYTES, minQuality = 0 } = {}) {
  if (!file || !(file.type || "").startsWith("image/")) return file;
  if (file.size <= targetBytes) return file;
  try {
    const img = await decodeToBitmap(file);
    const w = img.width || img.naturalWidth;
    const h = img.height || img.naturalHeight;
    if (!w || !h) return file;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    if (typeof img.close === "function") img.close();

    let blob = null;
    const steps = QUALITY_STEPS.filter((q) => q >= minQuality);
    if (!steps.length) steps.push(QUALITY_STEPS[0]);
    for (const q of steps) {
      blob = await canvasToBlob(canvas, q);
      if (blob && blob.size <= targetBytes) break;
    }
    if (!blob) return file;
    canvas.width = 0; canvas.height = 0; // release canvas memory immediately
    const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    // HEIC or other undecodable input — upload the original, backend accepts it.
    return file;
  }
}
